// Страница оформления самовывоза.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, CheckCircle, Truck } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import client from '../../api/client';
import { GARDEN_CENTER } from '../../constants/garden';
import { cleanPhoneInput, formatPhoneDisplay, normalizePhone, isPhoneComplete } from '../../utils/phone';
import { formatPrice } from '../../utils/price';
import { toast } from '../../components/Toast/toast';

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart } = useApp();

  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderType, setOrderType] = useState('');
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [showReserveConfirm, setShowReserveConfirm] = useState(false);
  const [reserveError, setReserveError] = useState('');
  const [activeReserves, setActiveReserves] = useState(0);

  // Авто-подстановка телефона из профиля
  useEffect(() => {
    client.get('/users/me')
      .then(res => {
        if (res.data.phone) setPhone(res.data.phone);
      })
      .catch(() => {});
  }, []);

  // Активные резервы клиента — для лимита «не более 3»
  useEffect(() => {
    client.get('/orders')
      .then(res => {
        const now = Date.now();
        const active = (res.data || []).filter(o =>
          o.status === 'reserved' && o.reserved_until &&
          new Date(o.reserved_until).getTime() > now);
        setActiveReserves(active.length);
      })
      .catch(() => {});
  }, []);

  // Смена корзины — сбрасываем ошибку предыдущей попытки
  useEffect(() => { setReserveError(''); }, [cart]);

  const total = cartTotal;
  const discount = Math.round(total * 0.02);
  const totalWithDiscount = total - discount;

  // Проверяем ограничение для резерва (не более 5 штук одного товара)
  const reserveOverLimit = cart.find(item => item.quantity > 5);
  // Лимиты резерва: 5 позиций в заказе, 3 активных резерва у клиента
  const reserveItemsOverLimit = cart.length > 5;
  // Все сработавшие причины — одним списком в едином блоке (спека S6)
  const reserveReasons = [];
  if (reserveOverLimit) {
    reserveReasons.push(
      `«${reserveOverLimit.name}» — ${reserveOverLimit.quantity} шт., доступно не более 5 штук одного товара`
    );
  }
  if (reserveItemsOverLimit) reserveReasons.push('В заказе не более 5 позиций');
  if (activeReserves >= 3) {
    reserveReasons.push('У вас уже 3 активных резерва — дождитесь их окончания или оплатите заказ');
  }
  if (reserveError) reserveReasons.push(reserveError);

  const handlePayOnline = async () => {
    if (!isPhoneComplete(phone) || cart.length === 0) return;
    setLoading(true);

    // Шаг 1: создаём заказ. Сразу очищаем корзину и на экране — сервер делает это
    // в момент создания заказа; без очистки повторное нажатие создало бы второй заказ.
    let orderId;
    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          variant_id: item.variant_id || undefined,
        })),
        delivery_type: 'pickup',
        phone: normalizePhone(phone),
        payment_type: 'online',
      };
      const res = await client.post('/orders', orderData);
      orderId = res.data?.id;
      await clearCart();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Ошибка при создании заказа. Попробуйте ещё раз.');
      setLoading(false);
      return;
    }

    // Шаг 2: открываем страницу оплаты. Заказ уже создан — сообщаем об этом отдельно.
    try {
      const returnUrl = `${window.location.origin}/checkout?payment=success&order_id=${orderId}`;
      const payRes = await client.post(`/payment/create?order_id=${orderId}&return_url=${encodeURIComponent(returnUrl)}`);
      window.location.href = payRes.data.confirmation_url;
    } catch (err) {
      toast.info(`Заказ #${typeof orderId === 'string' ? orderId.slice(-5) : String(orderId || '').slice(-5)} создан, но страница оплаты не открылась. Оплатить его можно по кнопке «💳 Оплатить» в боте.`);
      setLoading(false);
    }
  };

  const handleReserve = async () => {
    if (!isPhoneComplete(phone) || cart.length === 0) return;
    if (reserveOverLimit) return;

    setLoading(true);
    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          variant_id: item.variant_id || undefined,
        })),
        delivery_type: 'pickup',
        phone: normalizePhone(phone),
        payment_type: 'reserve',
      };
      const res = await client.post('/orders', orderData);
      const orderId = res.data?.id;
      setOrderNumber(typeof orderId === 'string' ? orderId.slice(-5) : String(orderId || '').slice(-5));
      setOrderType('reserved');
      setSuccess(true);
      clearCart();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setReserveError(typeof detail === 'string' ? detail : 'Не удалось создать резерв. Попробуйте ещё раз.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success' && params.get('order_id')) {
      const orderId = params.get('order_id');
      setOrderNumber(orderId.slice(-5));
      setOrderType('paid');
      setSuccess(true);
      clearCart();
      navigate('/checkout', { replace: true });
    }
  }, [clearCart, navigate]);

  if (success) {
    return (
      <div className="pb-4 animate-fade-in">
        <div className="flex min-h-[80vh] flex-col items-center justify-center px-4 text-center">
          <div className="mb-4 grid h-20 w-20 place-items-center rounded-full bg-green-sage/20">
            <CheckCircle className="h-10 w-10 text-green-primary" />
          </div>
          {orderType === 'paid' ? (
            <>
              <h2 className="text-xl font-bold text-green-dark mb-2">Заказ оплачен!</h2>
              <p className="text-sm text-green-muted mb-1">Номер заказа</p>
              <p className="text-lg font-bold text-green-primary mb-4">#{orderNumber}</p>
              <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-border-warm mb-6 text-left w-full max-w-xs">
                <p className="text-sm text-green-dark font-medium mb-2">Самовывоз</p>
                <p className="text-xs text-green-muted">{GARDEN_CENTER.address}</p>
                <p className="text-xs text-green-muted">{GARDEN_CENTER.workingHoursShort}</p>
                <p className="text-xs text-green-primary mt-2">Бронь на 24 часа</p>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-green-dark mb-2">Заказ зарезервирован!</h2>
              <p className="text-sm text-green-muted mb-1">Номер заказа</p>
              <p className="text-lg font-bold text-green-primary mb-4">#{orderNumber}</p>
              <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-yellow-soft mb-6 text-left w-full max-w-xs">
                <p className="text-sm text-terracotta font-medium mb-2">Условия резерва</p>
                <p className="text-xs text-green-muted mb-1.5">⏰ Храним для вас 4 часа</p>
                <p className="text-xs text-green-muted mb-1.5">🔔 Напомним в боте через 2 часа</p>
                <p className="text-xs text-green-muted">💛 Если не успеете — просто снимем бронь</p>
              </div>
            </>
          )}
          <button
            onClick={() => navigate('/')}
            className="w-full max-w-xs rounded-xl bg-terracotta py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90 active:scale-[0.98]"
          >
            Вернуться в каталог
          </button>
        </div>
      </div>
    );
  }

  if (showDeliveryForm) {
    return <DeliveryForm onBack={() => setShowDeliveryForm(false)} onCart={() => navigate('/cart')} />;
  }

  return (
    <div className="pb-4 animate-fade-in">
      {/* Шапка */}
      <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/cart')} className="grid h-9 w-9 place-items-center rounded-xl bg-cream-card text-green-muted">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-green-muted">Оформление</div>
            <div className="-mt-0.5 text-base font-bold text-green-dark">Самовывоз</div>
          </div>
        </div>
      </header>

      <div className="px-4 space-y-4 mt-2">
        {/* Адрес и часы */}
        <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-border-warm text-center">
          <p className="text-sm font-medium text-green-dark">{GARDEN_CENTER.address}</p>
          <p className="text-xs text-green-muted mt-1">{GARDEN_CENTER.workingHoursShort}</p>
        </div>

        {/* Телефон */}
        <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-border-warm">
          <h3 className="text-sm font-semibold text-green-dark mb-3">Телефон</h3>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-muted" />
            <input
              type="tel"
              placeholder="+7 (___) ___-__-__"
              value={phone}
              onChange={(e) => setPhone(cleanPhoneInput(e.target.value))}
              onBlur={() => setPhone(prev => formatPhoneDisplay(prev))}
              className="w-full rounded-xl border border-border-warm bg-cream py-2.5 pl-9 pr-3 text-sm outline-none focus:border-green-sage focus:bg-white"
            />
          </div>
        </div>

        {/* Итого */}
        <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-border-warm">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-green-muted">{cart.length} товар(ов)</span>
            <span className="text-base font-bold text-green-dark">{formatPrice(total)} ₽</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-green-primary">Скидка при оплате онлайн (2%)</span>
            <span className="text-xs text-green-primary">−{formatPrice(discount)} ₽</span>
          </div>
        </div>

        {/* Кнопка: Оплатить онлайн */}
        <button
          onClick={handlePayOnline}
          disabled={loading || !isPhoneComplete(phone) || cart.length === 0}
          className="w-full rounded-xl bg-terracotta py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Оформление...' : `Оплатить онлайн — ${formatPrice(totalWithDiscount)} ₽`}
        </button>

        {/* Выгоды оплаты */}
        <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-border-warm space-y-2">
          <p className="text-sm font-semibold text-green-dark">Выгода оплаты онлайн</p>
          <div className="flex items-center gap-2 text-xs text-green-dark">
            <span className="text-green-primary">✓</span> Скидка 2% на весь заказ
          </div>
          <div className="flex items-center gap-2 text-xs text-green-dark">
            <span className="text-green-primary">✓</span> Фиксация цены — оплатили, цена ваша
          </div>
          <div className="flex items-center gap-2 text-xs text-green-dark">
            <span className="text-green-primary">✓</span> Приоритетная сборка — собираем первыми
          </div>
        </div>

        {/* Ограничения резерва — единый блок со всеми сработавшими причинами */}
        {reserveReasons.length > 0 && (
          <div className="rounded-2xl bg-terracotta/10 p-4 ring-1 ring-terracotta/30">
            <p className="text-sm font-semibold text-terracotta mb-1">Ограничение на резерв</p>
            <ul className="list-inside list-disc space-y-1 text-xs text-green-dark">
              {reserveReasons.map((reason, idx) => (
                <li key={idx}>{reason}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Кнопка: Зарезервировать */}
        <button
          onClick={() => setShowReserveConfirm(true)}
          disabled={loading || !isPhoneComplete(phone) || cart.length === 0 || !!reserveOverLimit || reserveItemsOverLimit || activeReserves >= 3}
          className="w-full rounded-xl border border-terracotta py-3 text-sm font-medium text-terracotta transition hover:bg-terracotta/5 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Зарезервировать без оплаты
        </button>
        <p className="mt-2 text-center text-xs text-green-muted">
          Резерв: не более 5 позиций и 5 штук каждого, не более 3 активных резервов одновременно
        </p>

        {/* Условия резерва */}
        <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-yellow-soft/50 space-y-2">
          <p className="text-sm font-semibold text-terracotta">Условия резерва</p>
          <div className="text-xs text-green-muted">⏰ Храним для вас 4 часа</div>
          <div className="text-xs text-green-muted">🔔 Напомним в боте через 2 часа с кнопкой для оплаты</div>
          <div className="text-xs text-green-muted">💛 Если не успеете — не переживайте, просто снимем бронь</div>
        </div>

        {/* Доставка */}
        <div className="text-center py-3 border-t border-border-warm">
          <button
            onClick={() => setShowDeliveryForm(true)}
            className="text-sm text-green-muted underline transition hover:text-green-dark"
          >
            Нужна доставка? Оставьте заявку →
          </button>
        </div>
      </div>

      {/* Модалка подтверждения резерва */}
      {showReserveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setShowReserveConfirm(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-cream-card p-5 ring-1 ring-border-warm" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-green-dark mb-3">Вы уверены?</h3>
            <p className="text-sm text-green-muted mb-4">
              При онлайн-оплате вы получаете скидку 2%, фиксацию цены и приоритетную сборку.
              Зарезервировав без оплаты, вы теряете эти преимущества.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowReserveConfirm(false)}
                className="flex-1 rounded-xl border border-border-warm bg-cream py-2.5 text-sm font-medium text-green-dark transition hover:opacity-90 active:scale-[0.98]"
              >
                Оплатить онлайн
              </button>
              <button
                onClick={() => { setShowReserveConfirm(false); handleReserve(); }}
                disabled={loading}
                className="flex-1 rounded-xl bg-terracotta py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90 active:scale-[0.98] disabled:opacity-50"
              >
                {loading ? 'Резервирую...' : 'Зарезервировать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function DeliveryForm({ onBack, onCart }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Авто-подстановка данных из профиля
  useEffect(() => {
    client.get('/users/me')
      .then(res => {
        if (res.data.first_name) setName(res.data.first_name);
        if (res.data.phone) setPhone(res.data.phone);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!name.trim() || !isPhoneComplete(phone) || !address.trim()) return;
    setSending(true);
    try {
      await client.post('/delivery-request/', { name, phone: normalizePhone(phone), address, comment });
      setSent(true);
    } catch (err) {
      toast.error('Ошибка при отправке заявки. Попробуйте ещё раз.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="pb-4 animate-fade-in">
        <div className="flex min-h-[80vh] flex-col items-center justify-center px-4 text-center">
          <div className="mb-4 grid h-20 w-20 place-items-center rounded-full bg-green-sage/20">
            <CheckCircle className="h-10 w-10 text-green-primary" />
          </div>
          <h2 className="text-xl font-bold text-green-dark mb-2">Заявка отправлена!</h2>
          <p className="text-sm text-green-muted mb-8">Менеджер свяжется с вами в ближайшее время</p>
          <button
            onClick={onCart}
            className="w-full max-w-xs rounded-xl bg-terracotta py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90 active:scale-[0.98]"
          >
            Вернуться в корзину
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4 animate-fade-in">
      <header className="sticky top-0 z-30 bg-cream/85 px-4 pb-3 pt-4 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="grid h-9 w-9 place-items-center rounded-xl bg-cream-card text-green-muted">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-green-muted">Заявка</div>
            <div className="-mt-0.5 text-base font-bold text-green-dark">На доставку</div>
          </div>
        </div>
      </header>

      <div className="px-4 space-y-4 mt-2">
        <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-border-warm space-y-3">
          <input
            type="text"
            placeholder="Ваше имя *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-xl border border-border-warm bg-cream py-2.5 px-3 text-sm outline-none focus:border-green-sage focus:bg-white"
          />
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-muted" />
            <input
              type="tel"
              placeholder="+7 (___) ___-__-__"
              value={phone}
              onChange={(e) => setPhone(cleanPhoneInput(e.target.value))}
              onBlur={() => setPhone(prev => formatPhoneDisplay(prev))}
              className="w-full rounded-xl border border-border-warm bg-cream py-2.5 pl-9 pr-3 text-sm outline-none focus:border-green-sage focus:bg-white"
            />
          </div>
          <div className="relative">
            <Truck className="absolute left-3 top-3 h-4 w-4 text-green-muted" />
            <textarea
              placeholder="Адрес доставки *"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={2}
              className="w-full resize-none rounded-xl border border-border-warm bg-cream py-2.5 pl-9 pr-3 text-sm outline-none focus:border-green-sage focus:bg-white"
            />
          </div>
          <textarea
            placeholder="Комментарий (необязательно)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="w-full resize-none rounded-xl border border-border-warm bg-cream py-2.5 px-3 text-sm outline-none focus:border-green-sage focus:bg-white"
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={sending || !name.trim() || !isPhoneComplete(phone) || !address.trim()}
          className="w-full rounded-xl bg-terracotta py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? 'Отправка...' : 'Отправить заявку'}
        </button>
      </div>
    </div>
  );
}
